import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { t } from 'i18next'
import moment from 'moment-timezone'

import EmptyPage from '~/components/empty-page'
import ListPagination from '~/components/ListPagination'
import Loading from '~/components/Loading'
import LoadingPopUp from '~/components/LoadingPopUp'
import Table from '~/components/Table'
import { formatSearchString, isAdvertiser } from '~/helpers'
import { useAlert } from '~/hooks/useAlert'
import useFetchSWR from '~/hooks/useFetchSWR'
import { useShowWalletOriginsColumn } from '~/modules/retailMedia/hooks/useShowWalletOrigins'
import { useWalletByPair } from '~/modules/retailMedia/hooks/useWalletByPair'
import { reatailmediaApi } from '~/modules/retailMedia/services/api'
import { buildPairTransactionQuery } from '~/modules/retailMedia/services/checking-account/buildPairTransactionQuery'
import {
  setPageGroceryListTransaction,
  setPerPageGroceryListTransaction
} from '~/modules/retailMedia/store/grocery/wallet/actions'
import { useAppSelector } from '~/store/hooks'

import { buildOriginByIdMap } from '../WalletBalanceOrigin/mapOrigins'
import { schema } from './schemas/schemas.grocery'
import styles from './walletActivitySection.module.css'
import { WalletTransactionFilters } from './WalletTransactionFilters'

export const GroceryTransactionTable = () => {
  const dispatch = useDispatch()
  const showWalletOrigins = useShowWalletOriginsColumn()
  const { wallet, mappedOrigins } = useWalletByPair()
  const [originId, setOriginId] = useState('')

  const { startDate, endDate } = useAppSelector(state => state.datepickerHeader)
  const { walletType } = useAppSelector(state => state.filters.retailMedia)
  const {
    grocery: {
      wallet: {
        listTransaction: { page, perPage }
      }
    }
  } = useAppSelector(state => state.retailMedia)

  const isAdvertiserAccount = isAdvertiser()
  const counterpartId = isAdvertiserAccount
    ? wallet?.publisher_id
    : wallet?.advertiser_id
  const originById = useMemo(
    () => buildOriginByIdMap(mappedOrigins),
    [mappedOrigins]
  )

  useEffect(() => {
    dispatch(setPageGroceryListTransaction(1))
  }, [dispatch, originId, walletType])

  const queryParams = useMemo(
    () =>
      formatSearchString(
        buildPairTransactionQuery({
          isAdvertiserAccount,
          counterpartId,
          checkingAccountId: originId || null,
          page,
          quantity: perPage,
          transactionType: Array.isArray(walletType)
            ? undefined
            : walletType?.value,
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          userInfo: true
        })
      ),
    [
      counterpartId,
      endDate,
      isAdvertiserAccount,
      originId,
      page,
      perPage,
      startDate,
      walletType
    ]
  )

  const { data, error, isValidating } =
    useFetchSWR<CheckingAccountTransactionsResponse>({
      url: `${reatailmediaApi}/checking-account/transaction?${queryParams}`,
      shouldFetch: Boolean(counterpartId),
      refreshInterval: 60000,
      revalidateOnFocus: false
    })

  const [AlertOnError] = useAlert(error)

  const handlePagination = useCallback(
    (nextPage: number) => {
      dispatch(setPageGroceryListTransaction(nextPage))
    },
    [dispatch]
  )

  const handleItemsPerPageChange = useCallback(
    ({ value }: { value: number }) => {
      dispatch(setPerPageGroceryListTransaction(value))
    },
    [dispatch]
  )

  return (
    <>
      <LoadingPopUp isActive={isValidating} />

      <AlertOnError />

      <section
        className={styles.section}
        aria-labelledby="wallet-activity-heading"
        data-wallet-activity-section
      >
        <div className={styles.sectionHead}>
          <h2 id="wallet-activity-heading" className={styles.heading}>
            {t('rm:Movimentations')}
          </h2>
        </div>

        <div className={styles.sectionBody}>
          <Loading status={!data && isValidating}>
            {t('common:actions.loadingData')}...
          </Loading>

          <WalletTransactionFilters
            origins={mappedOrigins}
            originId={originId}
            onOriginChange={setOriginId}
            showOriginFilter={showWalletOrigins}
          />

          {!!data?.total && (
            <div className={styles.tableScroll}>
              <Table
                schema={schema({
                  originById,
                  showOriginColumn: showWalletOrigins
                })}
                data={data.data || []}
                total={data.total}
                sideBorder
              />
            </div>
          )}

          {data?.total === 0 && (
            <EmptyPage
              title={t('common:table.empty')}
              subtitle={t('common:table.emptyFiltered')}
            />
          )}

          <footer>
            <ListPagination
              total={data?.total || 0}
              label={t(
                `rm:${(data?.total || 0) > 1 ? 'transactions' : 'transaction'}`
              )}
              currentPage={page}
              itemsPerPage={perPage}
              menuPlacement="top"
              onClickPagination={handlePagination}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </footer>
        </div>
      </section>
    </>
  )
}
